import type {
	Dispatch,
	PointerEvent as ReactPointerEvent,
	SetStateAction,
} from "react"
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"

interface ListPreviews {
	suppressed: boolean
	openId: string | null
	setOpenId: Dispatch<SetStateAction<string | null>>
}

export const ListPreviewContext = createContext<ListPreviews | null>(null)

/**
 * Hover card props for one row. Inside a ListPreviewContext only one row's
 * preview is open at a time, so a row that never got its pointerleave (its
 * node was swapped while the results changed) can't leave a second card up.
 */
export function useRowPreview(id: string) {
	const list = useContext(ListPreviewContext)
	const [localOpen, setLocalOpen] = useState(false)

	if (!list) {
		return {
			open: localOpen,
			onOpenChange: setLocalOpen,
			onPointerMove: undefined,
		}
	}

	return {
		open: !list.suppressed && list.openId === id,
		onOpenChange: (open: boolean) =>
			list.setOpenId((current) =>
				open ? id : current === id ? null : current,
			),
		// pointerenter never fires when rows change under a resting pointer.
		onPointerMove: (event: ReactPointerEvent) => {
			if (event.pointerType !== "touch") list.setOpenId(id)
		},
	}
}

/**
 * Hides previews from the first scroll or row change until the pointer really
 * moves. Browsers send a synthetic mousemove at the same coordinates after a
 * scroll, so only a position change counts.
 */
export function useListPreviews(rowsKey: string) {
	const [suppressed, setSuppressed] = useState(false)
	const [openId, setOpenId] = useState<string | null>(null)
	const lastPointer = useRef<{ x: number; y: number } | null>(null)
	const cleanup = useRef<(() => void) | null>(null)

	const suppress = useCallback(() => {
		setSuppressed(true)
		setOpenId(null)
	}, [])

	const previousRowsKey = useRef(rowsKey)
	useEffect(() => {
		if (previousRowsKey.current === rowsKey) return
		previousRowsKey.current = rowsKey
		suppress()
	}, [rowsKey, suppress])

	const listRef = useCallback(
		(el: HTMLElement | null) => {
			cleanup.current?.()
			cleanup.current = null
			if (!el) return

			const onPointerMove = (event: PointerEvent) => {
				const last = lastPointer.current
				lastPointer.current = { x: event.clientX, y: event.clientY }
				if (last && last.x === event.clientX && last.y === event.clientY) return
				setSuppressed(false)
			}

			// Scroll does not bubble, and the issue list scrolls in its own viewport.
			el.addEventListener("scroll", suppress, { capture: true, passive: true })
			el.addEventListener("pointermove", onPointerMove)
			cleanup.current = () => {
				el.removeEventListener("scroll", suppress, { capture: true })
				el.removeEventListener("pointermove", onPointerMove)
			}
		},
		[suppress],
	)

	const value = useMemo(
		() => ({ suppressed, openId, setOpenId }),
		[suppressed, openId],
	)

	return { listRef, value }
}
